import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import logoWhite from "../../assets/Home/logo_w.png";
import logoBlue from "../../assets/Home/logo_b.png";

/**
 * Parti comuni alle pagine con la grafica della bozza (home e Certificazione Qapex):
 * icone, barra di navigazione, footer, certificato e comparsa delle sezioni allo scroll.
 * Con `home` i link alle sezioni sono ancore della pagina; altrove portano a /#sezione.
 */

export const Arrow = () => <svg><use href="#i-arrow" /></svg>;

export const Icons = () => (
  <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
    <symbol id="i-arrow" viewBox="0 0 24 24"><path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></symbol>
    <symbol id="i-chip" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="3" /><rect x="9" y="9" width="6" height="6" rx="1" /><path d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 14h3M1 9h3M1 14h3" /></symbol>
    <symbol id="i-shield" viewBox="0 0 24 24"><path d="M12 2l8 3v6c0 5-3.5 9-8 11-4.5-2-8-6-8-11V5z" /><path d="M8.5 12l2.5 2.5 4.5-5" /></symbol>
    <symbol id="i-cloud" viewBox="0 0 24 24"><path d="M17.5 19H7a5 5 0 1 1 1.3-9.8A6 6 0 0 1 20 11a4 4 0 0 1-2.5 8z" /><path d="M10 14l2 2 3-4" /></symbol>
    <symbol id="i-flow" viewBox="0 0 24 24"><circle cx="5" cy="6" r="2.5" /><circle cx="19" cy="6" r="2.5" /><circle cx="12" cy="18" r="2.5" /><path d="M7.5 6h9M6.3 8.2l4.4 7.6M17.7 8.2l-4.4 7.6" /></symbol>
    <symbol id="i-server" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M8 20h8M12 16v4M8 10h.01M12 10h.01M16 10h.01" /></symbol>
    <symbol id="i-devices" viewBox="0 0 24 24"><rect x="2" y="4" width="14" height="11" rx="2" /><rect x="15" y="8" width="7" height="13" rx="1.5" /><path d="M6 19h6" /></symbol>
    <symbol id="i-gauge" viewBox="0 0 24 24"><path d="M12 14l4-4" /><path d="M3.3 19a10 10 0 1 1 17.4 0" /></symbol>
    <symbol id="i-code" viewBox="0 0 24 24"><path d="M8 7l-5 5 5 5M16 7l5 5-5 5" /><path d="M10 13l1.5 1.5L14.5 11" /></symbol>
    <symbol id="i-mail" viewBox="0 0 24 24"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 6l-10 7L2 6" /></symbol>
    <symbol id="i-pin" viewBox="0 0 24 24"><path d="M12 22s8-7 8-13a8 8 0 1 0-16 0c0 6 8 13 8 13z" /><circle cx="12" cy="9" r="3" /></symbol>
    <symbol id="i-doc" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M7 8h10M7 12h10M7 16h6" /></symbol>
  </svg>
);

// Comparsa delle sezioni (.reveal) allo scroll
export const useReveal = (rootRef) => {
  useEffect(() => {
    const io = new IntersectionObserver(
      (es) => es.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); } }),
      { threshold: 0.12 }
    );
    rootRef.current.querySelectorAll(".reveal").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [rootRef]);
};

// Link a una sezione della home: ancora se siamo già in home, altrimenti /#sezione
const SectionLink = ({ home, id, className, onClick, children }) =>
  home
    ? <a className={className} href={`#${id}`} onClick={onClick}>{children}</a>
    : <Link className={className} to={`/#${id}`} onClick={onClick}>{children}</Link>;

const navLinks = (t) => [
  { id: "servizi", label: t("home.nav.services") },
  { id: "quality-assurance", label: t("home.nav.qa") },
  { id: "prodotti", label: t("home.nav.products") },
  { id: "referenze", label: t("home.nav.references") },
  { id: "modello", label: t("home.nav.about") },
  { to: "/work-with-us", label: t("home.nav.workWithUs") },
];

const NavItem = ({ link, home, className, onClick }) =>
  link.to
    ? <Link className={className} to={link.to} onClick={onClick}>{link.label}</Link>
    : <SectionLink home={home} id={link.id} className={className} onClick={onClick}>{link.label}</SectionLink>;

export function QapexNav({ t, home = false }) {
  const [solid, setSolid] = useState(false);
  const [drawer, setDrawer] = useState(false);
  // Fuori dalla home la prima voce riporta alla home
  const links = home ? navLinks(t) : [{ to: "/", label: t("home.nav.home") }, ...navLinks(t)];

  // Barra solida dopo lo scroll
  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setDrawer(false);

  return (
    <>
      <header className={`nav${solid ? " solid" : ""}`}>
        <div className="in">
          {home ? <a href="#top"><img src={logoWhite} alt="Qapex" /></a> : <Link to="/"><img src={logoWhite} alt="Qapex" /></Link>}
          <ul>{links.map((l) => <li key={l.label}><NavItem link={l} home={home} /></li>)}</ul>
          <SectionLink home={home} id="contatti" className="btn btn-primary cta">{t("home.nav.contact")}</SectionLink>
          <button type="button" className="burger" aria-label={t("home.nav.openMenu")} onClick={() => setDrawer(true)}>
            <svg viewBox="0 0 24 24"><path d="M4 7h16M4 12h16M4 17h10" /></svg>
          </button>
        </div>
      </header>
      <nav className={`drawer${drawer ? " open" : ""}`} aria-label={t("home.nav.menu")}>
        <div className="top">
          <img src={logoWhite} alt="Qapex" />
          <button type="button" className="burger" style={{ display: "flex" }} aria-label={t("home.nav.closeMenu")} onClick={close}>
            <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>
        {links.map((l) => <NavItem key={l.label} link={l} home={home} className="l" onClick={close} />)}
        <SectionLink home={home} id="contatti" className="btn btn-primary" onClick={close}>{t("home.nav.contact")}</SectionLink>
      </nav>
    </>
  );
}

export function QapexFooter({ t, home = false }) {
  const links = navLinks(t);
  return (
    <footer>
      <div className="wrap">
        <div className="ftop">
          <div className="brand"><img src={logoWhite} alt="Qapex" /><p>{t("home.footer.tagline")}</p></div>
          <div className="fcols">
            <div className="fcol"><b>{t("home.footer.navigate")}</b>{links.slice(0, 4).map((l) => <NavItem key={l.label} link={l} home={home} />)}</div>
            <div className="fcol"><b>{t("home.footer.company")}</b>{links.slice(4).map((l) => <NavItem key={l.label} link={l} home={home} />)}<SectionLink home={home} id="contatti">{t("home.footer.contacts")}</SectionLink></div>
            <div className="fcol"><b>{t("home.footer.legal")}</b><Link to="/privacy-policy">{t("home.footer.privacy")}</Link><Link to="/cookie-policy">{t("home.footer.cookies")}</Link><SectionLink home={home} id="contatti">{t("home.footer.legalNotes")}</SectionLink></div>
          </div>
        </div>
        <div className="fbot">
          <span>© {new Date().getFullYear()} {t("home.footer.rights")}</span>
          <span>
            <a href="https://www.linkedin.com/company/qapexit/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
            {" · "}
            <a href="https://www.instagram.com/qapex_srl/" target="_blank" rel="noopener noreferrer">Instagram</a>
          </span>
        </div>
      </div>
    </footer>
  );
}

// Esempio del certificato Qapex Quality Assured
export function Certificate({ t }) {
  const cert = "home.qa.cert";
  return (
    <div className="certificate">
      <svg className="seal" viewBox="0 0 112 112" aria-hidden="true">
        <circle cx="56" cy="56" r="54" fill="#040414" />
        <circle className="ring" cx="56" cy="56" r="46" fill="none" stroke="#4FC3F7" strokeWidth="1.5" strokeDasharray="3 4" />
        <circle cx="56" cy="56" r="34" fill="#168FC3" />
        <path d="M42 57l9 9 19-20" fill="none" stroke="#fff" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div className="ctop"><img src={logoBlue} alt="Qapex" /><span className="id">N. QX-2026-0142</span></div>
      <div className="ctitle">{t(`${cert}.certTitle`)}</div>
      <h4>Qapex Quality Assured</h4>
      <div className="proj">{t(`${cert}.project`)}</div>
      <div className="kpis">
        {["1.248", "96%", "0"].map((v, i) => <div className="kpi" key={v}><b>{v}</b><span>{t(`${cert}.kpis`)[i]}</span></div>)}
      </div>
      <div className="cfoot">
        <div className="sign"><b>QA Lead · Qapex S.r.l.</b>ISTQB® Certified Tester</div>
        <div className="sign" style={{ textAlign: "right" }}><b>{t(`${cert}.date`)}</b>{t(`${cert}.dateLabel`)}</div>
      </div>
    </div>
  );
}
